if (!customElements.get('cart-upsell')) {
  class CartUpsell extends HTMLElement {
    constructor() {
      super();
      this.container = this.querySelector("[data-drag-container]");
      this.dragParent = this.querySelector("[data-drag-parent]");
      this.autoCanScroll = this.hasAttribute("data-auto-can-scroll");
      this.dragInitialized = false;
      this.resizeObserver = null;

      this.prevButton = this.parentElement && this.parentElement.querySelector("[data-upsell-prev]");
      this.nextButton = this.parentElement && this.parentElement.querySelector("[data-upsell-next]");
      this.nav = this.prevButton && this.prevButton.closest(".zdrinks-cart-upsell__nav");

      this.handleResize = this.init.bind(this);

      if (!this.container || !this.dragParent) return;

      if (this.autoCanScroll) {
        this.init();
        window.addEventListener("resize", this.handleResize);
      } else {
        this.enableScroller();
      }

      this.initNav();
    }

    disconnectedCallback() {
      window.removeEventListener("resize", this.handleResize);

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    }

    initNav() {
      if (!this.prevButton && !this.nextButton) return;

      const handleNavUpdate = this.updateNavState.bind(this);

      if (this.prevButton) {
        this.prevButton.addEventListener("click", () => this.scrollByCard(-1));
      }

      if (this.nextButton) {
        this.nextButton.addEventListener("click", () => this.scrollByCard(1));
      }

      this.getSlider().addEventListener("scroll", handleNavUpdate, { passive: true });

      this.updateNavState();
    }

    getSlider() {
      return this.isOverflowing(this.container) ? this.container : this.dragParent;
    }

    scrollByCard(direction) {
      const slider = this.getSlider();
      const card = slider.querySelector(".zdrinks-cart-upsell__card");
      const gap = parseFloat(window.getComputedStyle(slider).columnGap) || 0;
      const step = card ? card.offsetWidth + gap : slider.clientWidth;

      slider.scrollBy({ left: direction * step, behavior: "smooth" });
    }

    updateNavState() {
      if (!this.prevButton && !this.nextButton) return;

      const slider = this.getSlider();
      const canScroll = slider.scrollWidth > slider.clientWidth + 1;

      if (this.nav) this.nav.style.display = canScroll ? "" : "none";

      const atStart = !canScroll || slider.scrollLeft <= 1;
      const atEnd = !canScroll || Math.ceil(slider.scrollLeft + slider.clientWidth) >= slider.scrollWidth - 1;

      if (this.prevButton) this.prevButton.disabled = atStart;
      if (this.nextButton) this.nextButton.disabled = atEnd;
    }

    init() {
      this.resetLayout();

      const result = this.checkFlexWrapRows(this.dragParent);

      if (!result.isFlexWrap || !result.moreThan2Rows) {
        this.showSection();
        this.resetLayout();
        return;
      }

      this.enableScroller();

      if (result.isFlexWrap && result.moreThan2Rows) {

        this.dragParent.style.setProperty('flex-wrap', 'nowrap');
        this.dragParent.style.setProperty('justify-content', 'flex-start');

        this.initDragToScroll();

        this.container.setAttribute(
          "style",
          "--width: " + this.container.clientWidth + "px;"
        );

        window.addEventListener("resize", () => {
          this.container.setAttribute(
            "style",
            "--width: " + this.container.clientWidth + "px;"
          );
        });
        this.showSection();
      } else {
        this.container.removeAttribute("style");
        this.dragParent.style.removeProperty('flex-wrap');
        this.dragParent.style.removeProperty('justify-content');
      }
    }

    showSection() {
      requestAnimationFrame(() => {
        this.classList.add("is-ready");
      });
    }

    enableScroller() {
      this.dragParent.style.setProperty("flex-wrap", "nowrap");
      this.dragParent.style.setProperty("justify-content", "flex-start");

      this.updateContainerWidth();

      if (!this.resizeObserver) {
        this.resizeObserver = new ResizeObserver(() => {
          this.updateContainerWidth();
          this.updateNavState();
        });

        this.resizeObserver.observe(this.container);
      }

      if (!this.dragInitialized) {
        this.initDragToScroll();
        this.dragInitialized = true;
      }
    }

    resetLayout() {
      this.container.removeAttribute("style");
      this.dragParent.style.removeProperty("flex-wrap");
      this.dragParent.style.removeProperty("justify-content");
    }

    updateContainerWidth() {
      this.container.style.setProperty(
        "--width",
        `${this.container.clientWidth}px`
      );
    }

    initDragToScroll() {
      const target = this.isOverflowing(this.container)
        ? this.container
        : this.dragParent;

      this.dragToScroll(target);
    }

    checkFlexWrapRows(element) {
      if (!element) return {
        isFlexWrap: false,
        rows: 0,
        moreThan2Rows: false
      };

      const style = window.getComputedStyle(element);

      const isFlexWrap =
        style.display.includes('flex') &&
        style.flexWrap !== 'nowrap';


      if (!isFlexWrap) {
        return {
          isFlexWrap: false,
          rows: 1,
          moreThan2Rows: false
        };
      }

      const items = [...element.children].filter(el => el.offsetParent !== null);
      const rowTops = items.map(item => Math.round(item.getBoundingClientRect().top));

      const rows = new Set(rowTops).size;

      return {
        isFlexWrap: true,
        rows,
        moreThan2Rows: rows > 2
      };
    }

    isOverflowing(element) {
      return element.scrollWidth > element.clientWidth;
    }

    dragToScroll(slider) {
      let mouseDown = false;
      let start;
      let scrollLeft;
      let inactiveTimeout;

      slider.addEventListener("mousedown", (e) => {
        const target = e.target;

        mouseDown = true;
        start = e.pageX - slider.offsetLeft;
        scrollLeft = slider.scrollLeft;
      });

      slider.addEventListener("mouseup", () => {
        mouseDown = false;

        clearTimeout(inactiveTimeout);
        inactiveTimeout = setTimeout(() => {
          slider.classList.remove("active");
        }, 150);
      });

      slider.addEventListener("mousemove", (e) => {
        if (!mouseDown) return;
        e.preventDefault();

        if (!slider.classList.contains("active")) {
          slider.classList.add("active");
        }

        const x = e.pageX - slider.offsetLeft;
        const walk = (x - start) * 1;
        slider.scrollLeft = scrollLeft - walk;
      });

      slider.addEventListener("mouseleave", () => {
        mouseDown = false;

        clearTimeout(inactiveTimeout);
        inactiveTimeout = setTimeout(() => {
          slider.classList.remove("active");
        }, 150);
      });
    }

    slide(direction) {
      const slider = this.isOverflowing(this.container)
        ? this.container
        : this.dragParent;

      slider.scrollBy({
        left: direction * slider.clientWidth,
        behavior: "smooth",
      });
    }

    debounce(callback, delay = 150) {
      let timeout;

      return (...args) => {
        clearTimeout(timeout);

        timeout = setTimeout(() => {
          callback.apply(this, args);
        }, delay);
      };
    }
  }

  customElements.define("cart-upsell", CartUpsell);
}